import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, InputPrimitive, isWeb } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  useId,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";

/**
 * Display variant of the InputColor control. Maps 1:1 to the Figma `Type` prop:
 * - `color`      → "Color only"          (swatch preview only, no editable field)
 * - `color-hex`  → "Color + HEX"         (swatch + editable hex field)
 * - `color-rgb`  → "Color + HSL/HSB/RGB" (swatch + editable R/G/B channels)
 * - `hex`        → "HEX"                  (editable hex field only)
 * - `rgb`        → "HSL/HSB/RGB"          (editable R/G/B channels only)
 */
export type InputColorType =
  | "color"
  | "color-hex"
  | "color-rgb"
  | "hex"
  | "rgb";

export type InputColorSize = "xl" | "lg" | "md" | "sm" | "xs";

export interface InputColorProps extends ThemeOverrideProps {
  /**
   * The current color value as a hex string (controlled mode), e.g. `#22A8C3`.
   * When `transparency` is enabled an 8-digit hex (`#RRGGBBAA`) is accepted.
   */
  value?: string;
  /**
   * The initial color value for uncontrolled usage. Defaults to `#000000`.
   */
  defaultValue?: string;
  /**
   * Event handler fired when the color changes. Always receives a hex string.
   */
  onChange?: (value: string) => void;
  /**
   * Fired when the swatch is activated. The swatch is only interactive when
   * `type="color"` — its role is to open the parent ColorPicker panel. For all
   * other types the swatch is a decorative preview and this is never called.
   */
  onSwatchClick?: () => void;
  /**
   * Display variant of the control. Defaults to `color-hex`.
   */
  type?: InputColorType;
  /**
   * Enables the alpha channel. Adds an alpha field and splits the swatch to
   * preview the transparency against a checkerboard.
   */
  transparency?: boolean;
  /**
   * Property for changing the size of the control.
   */
  size?: InputColorSize;
  /**
   * Property for displaying a label above the control.
   */
  label?: string;
  /**
   * Unique identifier for the control. Used for accessibility linking.
   */
  id?: string;
  /**
   * Accessible label for screen readers when no visible label is present.
   */
  "aria-label"?: string;
  testID?: string;
}

type Rgba = { r: number; g: number; b: number; a: number };

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max);

const HEX_CHARS = /[^0-9a-fA-F]/g;

/** Parse a `#RGB`/`#RGBA`/`#RRGGBB`/`#RRGGBBAA` hex string into RGBA. */
function hexToRgba(hex: string): Rgba {
  let h = (hex || "").replace(/^#/, "");
  if (h.length === 3 || h.length === 4) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = h.length >= 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
  return {
    r: Number.isNaN(r) ? 0 : r,
    g: Number.isNaN(g) ? 0 : g,
    b: Number.isNaN(b) ? 0 : b,
    a: Number.isNaN(a) ? 1 : parseFloat(a.toFixed(2)),
  };
}

/** Serialize RGBA back to an uppercase hex string. */
function rgbaToHex({ r, g, b, a }: Rgba, includeAlpha: boolean): string {
  const toHex = (n: number) =>
    Math.round(clamp(n, 0, 255))
      .toString(16)
      .padStart(2, "0");
  const alpha = includeAlpha ? toHex(clamp(a, 0, 1) * 255) : "";
  return `#${toHex(r)}${toHex(g)}${toHex(b)}${alpha}`.toUpperCase();
}

const SWATCH_SIZE: Record<InputColorSize, number> = {
  xl: 36,
  lg: 32,
  md: 26,
  sm: 20,
  xs: 14,
};

// Width of the trailing alpha ("%") segment per size, derived from the Figma
// "Color only / Transparency=True" widths (total − swatch square − 2px gap).
const ALPHA_WIDTH: Record<InputColorSize, number> = {
  xl: 96,
  lg: 86,
  md: 77,
  sm: 67,
  xs: 58,
};

// Gap between the swatch and the value content inside a field, from the Figma
// `input/<size>/frame/gap` tokens.
const FRAME_GAP: Record<InputColorSize, number> = {
  xl: 10,
  lg: 10,
  md: 8,
  sm: 6,
  xs: 4,
};

// CSS checkerboard, only visible behind partially-transparent colors.
const CHECKERBOARD =
  "conic-gradient(rgba(0,0,0,0.45) 25%, transparent 0 50%, rgba(0,0,0,0.45) 0 75%, transparent 0)";

type Corner = "left" | "right" | "middle" | "all";

function cornerRadii(corner: Corner, radius: number) {
  const map = {
    all: [radius, radius, radius, radius],
    left: [radius, 0, 0, radius],
    right: [0, radius, radius, 0],
    middle: [0, 0, 0, 0],
  } as const;
  const [tl, tr, br, bl] = map[corner];
  return {
    borderTopLeftRadius: tl,
    borderTopRightRadius: tr,
    borderBottomRightRadius: br,
    borderBottomLeftRadius: bl,
  };
}

/* -------------------------------------------------------------------------- */
/*                              Color swatch                                  */
/* -------------------------------------------------------------------------- */

function ColorSwatch({
  size,
  rgba,
  transparency,
  borderColor,
}: {
  size: number;
  rgba: Rgba;
  transparency: boolean;
  borderColor: string;
}) {
  const solid = `rgb(${rgba.r}, ${rgba.g}, ${rgba.b})`;
  const withAlpha = `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
  return (
    <Box
      width={size}
      height={size}
      position="relative"
      borderColor={borderColor}
      borderWidth={1}
      style={{ borderRadius: 4, overflow: "hidden" }}
      data-testid="input-color__swatch-preview"
    >
      {transparency && (
        <Box
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: CHECKERBOARD,
            backgroundSize: "8px 8px",
            opacity: 0.2,
          }}
        />
      )}
      {transparency ? (
        <>
          <Box
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: "50%",
              backgroundColor: solid,
            }}
          />
          <Box
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "50%",
              right: 0,
              backgroundColor: withAlpha,
            }}
          />
        </>
      ) : (
        <Box
          style={{ position: "absolute", inset: 0, backgroundColor: solid }}
        />
      )}
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Editable text field                             */
/* -------------------------------------------------------------------------- */

type TextFieldProps = {
  value: string;
  onCommit: (text: string) => void;
  sanitize: (text: string) => string;
  maxLength?: number;
  textAlign?: "left" | "center";
  color: string;
  fontSize: number;
  fontFamily: string;
  ariaLabel: string;
  inputMode?: "numeric" | "text";
  /** When set, Arrow Up/Down step the numeric value within [min, max]. */
  step?: { min: number; max: number };
  testID?: string;
  onFocus: () => void;
  onBlur: () => void;
};

/**
 * A controlled-but-buffered text field. Keeps the raw keystrokes locally so the
 * user can type freely, but resyncs to the canonical `value` on prop change and
 * on blur.
 */
function TextField({
  value,
  onCommit,
  sanitize,
  maxLength,
  textAlign = "left",
  color,
  fontSize,
  fontFamily,
  ariaLabel,
  inputMode = "text",
  step,
  testID,
  onFocus,
  onBlur,
}: TextFieldProps) {
  const [text, setText] = useState(value);
  // While the field is focused we keep the user's raw keystrokes and never
  // overwrite them with the canonical (normalized/expanded/clamped) value —
  // that resync is what caused the field to "auto-fill" mid-typing. We only
  // resync from `value` for external changes (unfocused) and on blur.
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) setText(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = sanitize(e.target.value);
    setText(next);
    onCommit(next);
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
    onFocus();
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    setText(value);
    onBlur();
  };

  // Enter commits/normalizes by blurring (the value already propagates live).
  // Arrow Up/Down step numeric fields by ±1 within their range.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
      return;
    }
    if (step && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      const current = Number.parseInt(text, 10);
      const base = Number.isNaN(current) ? 0 : current;
      const next = String(
        clamp(base + (e.key === "ArrowUp" ? 1 : -1), step.min, step.max)
      );
      setText(next);
      onCommit(next);
    }
  };

  return (
    <Box flex={1} height="100%" justifyContent="center">
      <InputPrimitive
        value={text}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        type="text"
        inputMode={inputMode}
        maxLength={maxLength}
        color={color}
        fontSize={fontSize}
        fontFamily={fontFamily}
        aria-label={ariaLabel}
        data-testid={testID}
        style={textAlign === "center" ? { textAlign: "center" } : undefined}
      />
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/*                                InputColor                                  */
/* -------------------------------------------------------------------------- */

const CHANNEL_LABEL: Record<"red" | "green" | "blue" | "alpha", string> = {
  red: "Red channel",
  green: "Green channel",
  blue: "Blue channel",
  alpha: "Opacity percentage",
};

export const InputColor = forwardRef<HTMLDivElement, InputColorProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      onSwatchClick,
      type = "color-hex",
      transparency = false,
      size = "md",
      label,
      id: providedId,
      "aria-label": ariaLabel,
      testID,
      themeMode,
      themeProductContext,
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });

    // Mirrors the Input "useUpdatableState" pattern: always keep an internal
    // value so the swatch and fields reflect edits immediately — even if a
    // controlled parent doesn't echo onChange back — and sync from `value`
    // whenever it changes externally.
    const [internalValue, setInternalValue] = useState(
      value ?? defaultValue ?? "#000000"
    );
    useEffect(() => {
      if (value !== undefined) setInternalValue(value);
    }, [value]);
    const currentValue = internalValue;

    // Which segment currently owns focus (for outline styling).
    const [focusedKey, setFocusedKey] = useState<string | null>(null);

    // Polite live-region message announced to screen readers on each edit.
    const [liveMessage, setLiveMessage] = useState("");

    const rawId = useId();
    const safeId = rawId.replace(/:/g, "");
    const inputId = providedId || `input-color-${safeId}`;
    const labelId = `${inputId}-label`;

    const rgba = useMemo(() => hexToRgba(currentValue), [currentValue]);

    const sizeStyles = theme.sizing.input(size);
    const inputColors = theme.colors.control.input;
    const borderRadius = theme.shape.input[size].borderRadius;
    const swatchSize = SWATCH_SIZE[size];

    const showSwatch =
      type === "color" || type === "color-hex" || type === "color-rgb";
    const isHex = type === "hex" || type === "color-hex";
    const isRgb = type === "rgb" || type === "color-rgb";
    const isSwatchOnly = type === "color";

    const textColor = inputColors.text;
    const placeholderColor = inputColors.placeholder;
    const fontFamily = theme.fonts.body;

    const emit = (next: string) => {
      setInternalValue(next);
      setLiveMessage(`Color updated to ${next}`);
      onChange?.(next);
    };

    const emitFromRgba = (next: Rgba) => emit(rgbaToHex(next, transparency));

    /* ---- per-segment visual state ---- */
    const segmentVisuals = (focused: boolean) => {
      let backgroundColor = inputColors.bg;
      let borderColor = inputColors.border;
      let outlineColor: string | undefined;
      if (focused) {
        backgroundColor = theme.colors.control.focus.bg;
        outlineColor = theme.colors.border.brand;
      }
      return { backgroundColor, borderColor, outlineColor };
    };

    const renderSegment = (
      key: string,
      corner: Corner,
      content: React.ReactNode,
      opts: { flex?: number; width?: number; square?: boolean } = {}
    ) => {
      const focused = focusedKey === key;
      const { backgroundColor, borderColor, outlineColor } =
        segmentVisuals(focused);
      const width = opts.square ? sizeStyles.height : opts.width;
      return (
        <Box
          key={key}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          borderWidth={1}
          height={sizeStyles.height}
          width={width}
          flex={opts.flex}
          flexDirection="row"
          alignItems="center"
          justifyContent={opts.square ? "center" : "flex-start"}
          gap={FRAME_GAP[size]}
          paddingHorizontal={opts.square ? 0 : sizeStyles.paddingHorizontal}
          position="relative"
          style={{
            ...cornerRadii(corner, borderRadius),
            minWidth: opts.square ? undefined : 40,
            ...(outlineColor && isWeb
              ? { outline: `1px solid ${outlineColor}`, outlineOffset: "-1px" }
              : outlineColor && !isWeb
                ? { borderColor: outlineColor }
                : {}),
          }}
          hoverStyle={
            !focused
              ? {
                  backgroundColor: inputColors.bgHover,
                  borderColor: inputColors.borderHover,
                }
              : undefined
          }
        >
          {content}
        </Box>
      );
    };

    /* ---- swatch node ----
     * Per the Figma spec the swatch is only interactive when type="color": it
     * acts as the trigger that opens the parent ColorPicker panel (via
     * onSwatchClick). For every other type it is a decorative preview. */
    const swatchVisual = (
      <ColorSwatch
        size={swatchSize}
        rgba={rgba}
        transparency={transparency}
        borderColor={theme.colors.border.secondary}
      />
    );
    const swatchNode = isSwatchOnly ? (
      <Box
        as="button"
        type="button"
        onPress={() => onSwatchClick?.()}
        cursor="pointer"
        backgroundColor="transparent"
        borderWidth={0}
        padding={0}
        alignItems="center"
        justifyContent="center"
        aria-label="Open color picker"
        data-testid="input-color__swatch"
        onFocus={() => setFocusedKey("swatch")}
        onBlur={() => setFocusedKey(null)}
      >
        {swatchVisual}
      </Box>
    ) : (
      <Box
        aria-hidden
        alignItems="center"
        justifyContent="center"
        data-testid="input-color__swatch-decorative"
      >
        {swatchVisual}
      </Box>
    );

    /* ---- hex field content ---- */
    const hexContent = (
      <>
        <Text
          color={placeholderColor}
          fontSize={sizeStyles.fontSize}
          style={{ lineHeight: `${sizeStyles.lineHeight}px` }}
        >
          #
        </Text>
        <TextField
          // Hex field is always the RGB triplet; alpha is edited via its own
          // "%" segment, so the value never carries an alpha pair here.
          value={rgbaToHex(rgba, false).replace(/^#/, "")}
          sanitize={(t) => t.replace(HEX_CHARS, "").toUpperCase()}
          maxLength={6}
          onCommit={(digits) => {
            if (digits.length === 3 || digits.length === 6) {
              const next = hexToRgba(`#${digits}`);
              emitFromRgba({ ...next, a: rgba.a });
            }
          }}
          color={textColor}
          fontSize={sizeStyles.fontSize}
          fontFamily={fontFamily}
          ariaLabel="Hex color value"
          testID="input-color__hex"
          onFocus={() => setFocusedKey("hex")}
          onBlur={() => setFocusedKey(null)}
        />
      </>
    );

    /* ---- channel field ---- */
    const channelContent = (channel: "red" | "green" | "blue" | "alpha") => {
      const isAlpha = channel === "alpha";
      const display = isAlpha
        ? Math.round(rgba.a * 100)
        : rgba[channel[0] as "r" | "g" | "b"];
      const field = (
        <TextField
          value={String(display)}
          inputMode="numeric"
          sanitize={(t) => t.replace(/[^0-9]/g, "")}
          maxLength={3}
          step={isAlpha ? { min: 0, max: 100 } : { min: 0, max: 255 }}
          onCommit={(t) => {
            if (t === "") return;
            const num = parseInt(t, 10);
            if (Number.isNaN(num)) return;
            if (isAlpha) {
              emitFromRgba({ ...rgba, a: clamp(num, 0, 100) / 100 });
            } else {
              const k = channel[0] as "r" | "g" | "b";
              emitFromRgba({ ...rgba, [k]: clamp(num, 0, 255) });
            }
          }}
          color={textColor}
          fontSize={sizeStyles.fontSize}
          fontFamily={fontFamily}
          ariaLabel={CHANNEL_LABEL[channel]}
          testID={`input-color__channel-${channel}`}
          onFocus={() => setFocusedKey(`channel-${channel}`)}
          onBlur={() => setFocusedKey(null)}
        />
      );
      if (!isAlpha) return field;
      return (
        <>
          {field}
          <Text
            color={placeholderColor}
            fontSize={sizeStyles.fontSize}
            style={{ lineHeight: `${sizeStyles.lineHeight}px` }}
          >
            %
          </Text>
        </>
      );
    };

    /* ---- assemble control by type ----
     * Build an ordered list of cells, then enabling `transparency` simply
     * appends a trailing alpha ("%") cell. Corners are rounded across the whole
     * list: a single cell is fully rounded; otherwise first → left, last →
     * right, the rest square. */
    type Cell = {
      key: string;
      content: React.ReactNode;
      flex?: number;
      width?: number;
      square?: boolean;
    };
    const cells: Cell[] = [];

    if (isSwatchOnly) {
      cells.push({ key: "swatch", content: swatchNode, square: true });
    } else if (isHex) {
      cells.push({
        key: "field",
        flex: 1,
        content: (
          <>
            {showSwatch && swatchNode}
            <Box flex={1} flexDirection="row" alignItems="center" gap={4}>
              {hexContent}
            </Box>
          </>
        ),
      });
    } else if (isRgb) {
      if (showSwatch) {
        cells.push({ key: "swatch", content: swatchNode, square: true });
      }
      (["red", "green", "blue"] as const).forEach((channel) =>
        cells.push({
          key: `channel-${channel}`,
          content: channelContent(channel),
          flex: 1,
        })
      );
    }

    if (transparency) {
      cells.push({
        key: "channel-alpha",
        content: channelContent("alpha"),
        width: ALPHA_WIDTH[size],
      });
    }

    const control = cells.map((cell, i) => {
      const corner: Corner =
        cells.length === 1
          ? "all"
          : i === 0
            ? "left"
            : i === cells.length - 1
              ? "right"
              : "middle";
      return renderSegment(cell.key, corner, cell.content, {
        flex: cell.flex,
        width: cell.width,
        square: cell.square,
      });
    });

    return (
      <Box
        ref={ref}
        flexDirection="column"
        gap={sizeStyles.fieldGap}
        width={isSwatchOnly ? undefined : "100%"}
        testID={testID}
      >
        {label && (
          <Box as="label" id={labelId}>
            <Text
              color={theme.colors.content.secondary}
              fontSize={sizeStyles.fontSize - 2}
              fontWeight="500"
            >
              {label}
            </Text>
          </Box>
        )}
        <Box
          flexDirection="row"
          alignItems="center"
          gap={2}
          width={isSwatchOnly ? undefined : "100%"}
          role="group"
          aria-labelledby={label ? labelId : undefined}
          aria-label={!label ? ariaLabel : undefined}
        >
          {control}
        </Box>
        {isWeb && (
          <Box
            role="status"
            aria-live="polite"
            data-testid="input-color__live"
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          >
            {liveMessage}
          </Box>
        )}
      </Box>
    );
  }
);

InputColor.displayName = "InputColor";
